import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography
} from '@mui/material';
import { useMutation } from 'react-query';
import { toast } from 'sonner';
import client from '../../../../../API';

const DeliverableDialog = ({ open, onClose, info }) => {
  const [formData, setFormData] = useState({
    partnership_id: '',
    creator_id: '',
    deliverable_type: 'offer',
    deliverable_link: '',
    amount: '',
    status: 'sent',
    notes: '',
    due_date: null,
    po_number: '',
    script: '',
    revision: null
  });

  // Initialize form data when editing
  useEffect(() => {
    if (info) {
      setFormData({
        partnership_id: info.partnership_id || '',
        creator_id: info.creator_id || '',
        deliverable_type: info.deliverable_type || 'offer',
        deliverable_link: info.deliverable_link || '',
        amount: info.amount || '',
        status: info.status || 'sent',
        notes: info.notes || '',
        due_date: info.due_date ? new Date(info.due_date) : null,
        po_number: info.po_number || '',
        script: info.script || '',
        revision: info.revision || null
      });
    }
  }, [info]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      due_date: date
    });
  };

  const updateDeliverableMutation = useMutation(
    (data) => client.deliverables.update(info.id, data),
    {
      onSuccess: () => {
        toast.success('Deliverable updated successfully');
        onClose(true); // true indicates refresh needed
      },
      onError: (error) => {
        console.error('Error updating deliverable:', error);
        toast.error('Failed to update deliverable', {
          description: error?.response?.data?.error || error.message
        });
      }
    }
  );

  const handleSubmit = () => {
    // Basic validation
    if (!formData.partnership_id || !formData.creator_id || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    // Prepare data for submission
    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount),
      revision: formData.revision ? parseInt(formData.revision) : null,
      due_date: formData.due_date ? formData.due_date.toISOString() : null
    };
    if (info) {
      // Editing existing deliverable
      updateDeliverableMutation.mutate(submitData);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Deliverable
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Partnership ID"
                required
                type="number"
                value={formData.partnership_id}
                onChange={handleChange('partnership_id')}
                disabled={!!info} // Can't change partnership once created
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Creator ID"
                required
                value={formData.creator_id}
                onChange={handleChange('creator_id')}
                disabled={!!info} // Can't change creator once created
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Deliverable Type</InputLabel>
                <Select
                  value={formData.deliverable_type}
                  label="Deliverable Type"
                  onChange={handleChange('deliverable_type')}
                >
                  <MenuItem value="offer">Offer</MenuItem>
                  <MenuItem value="bonus">Bonus</MenuItem>
                  <MenuItem value="revision">Revision</MenuItem>
                  <MenuItem value="additional_work">Additional Work</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                required
                type="number"
                value={formData.amount}
                onChange={handleChange('amount')}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Payment amount in dollars"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleChange('status')}
                >
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Number"
                value={formData.po_number}
                onChange={handleChange('po_number')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Revision Number"
                type="number"
                value={formData.revision || ''}
                onChange={handleChange('revision')}
                inputProps={{ min: 0 }}
                helperText="Optional revision counter"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="datetime-local"
                value={formData.due_date ? formData.due_date.toISOString().slice(0, 16) : ''}
                onChange={(e) => handleDateChange(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Deliverable Link"
                value={formData.deliverable_link}
                onChange={handleChange('deliverable_link')}
                placeholder="https://..."
                helperText="Link to the deliverable content"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Script"
                multiline
                rows={3}
                value={formData.script}
                onChange={handleChange('script')}
                helperText="Optional script or content guidelines"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange('notes')}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateDeliverableMutation.isLoading}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliverableDialog;